import { format, isToday } from "date-fns";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { HasCancellationsIcon } from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/TrainSummary/HasCancellationsIcon";
import { DateSpan } from "features/VehicleSidebar/VehicleList/common/DateSpan";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { Header } from "features/VehicleSidebar/VehicleList/common/Header";
import { RightContent } from "features/VehicleSidebar/VehicleList/TrainList/RightContent";
import { TrainData } from "features/VehicleSidebar/VehicleList/TrainList/types";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { CircleIndicator } from "shared/components/dataDisplay/CircleIndicator";
import { Box } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { formatDateString } from "shared/utils/dateTimeUtils";
import * as ROUTES from "shared/utils/routes";
import { calcDelayColor } from "shared/utils/semantics";
import { useTheme } from "styled-components";

type Props = {
  train: TrainData;
  trainElementSelected: boolean;
  internalMessages: InternalMessageResponse[] | undefined;
};

export const TrainListElement = ({
  train,
  trainElementSelected,
  internalMessages,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const trainHasDelay = train.delay !== null && train.delay !== 0;
  const trainDateIsToday = isToday(new Date(train.trainDate));

  const originTime = train.originScheduledDepartureTime
    ? format(new Date(train.originScheduledDepartureTime), "HH:mm")
    : "";

  const destinationTime = train.destinationScheduledArrivalTime
    ? format(new Date(train.destinationScheduledArrivalTime), "HH:mm")
    : "";

  const eventNames =
    train.trainEventsTitles?.sort((a, b) => a.priority - b.priority) ?? [];

  return (
    <ElementWrapper
      to={generatePath(ROUTES.TRAIN, {
        countryCode: train.countryCode,
        trainNumber: train.trainNumber,
        trainDate: train.trainDate,
      })}
      selected={trainElementSelected}
      isHover
    >
      <Box
        display="grid"
        gridTemplateColumns="auto 1fr"
        alignItems="center"
        columnGap="12px"
        rowGap="3px"
        flex={1}
        minW="max-content"
        _focusVisible={{
          borderRadius: "6px",
        }}
      >
        <CircleIndicator $color={calcDelayColor(train.delay, theme)} />
        <Header>
          <Text bold>{train.trainNumber}</Text>
          {trainHasDelay && (
            <Text secondary>
              {train.delay} {t("min")}
            </Text>
          )}
          {train.hasCancellations && (
            <HasCancellationsIcon countryCode={train.countryCode} withTooltip />
          )}
        </Header>

        <Text
          style={{
            gridColumn: "2",
          }}
        >
          {train.originStopName}—{train.destinationStopName}
        </Text>
        <Text style={{ gridColumn: "2" }}>
          {originTime} - {destinationTime}
          {!trainDateIsToday && (
            <DateSpan>
              ({formatDateString(train.trainDate, "dd.MM.yy")})
            </DateSpan>
          )}
        </Text>
      </Box>
      <RightContent
        train={train}
        eventNames={eventNames}
        internalMessages={internalMessages}
      />
    </ElementWrapper>
  );
};
